// URL utilities for safely rendering AssistanceProgram (and similar)
// external links and decorating them with UTM tracking parameters.
//
// Each helper encodes ONE rule from RFC 3986 / WHATWG URL or one product
// convention, and is unit-tested in urlFormatter.test.js. Compose them in
// callers; do not re-derive the regexes inline.

// Default UTM params for outbound AP links — the single source of truth so analytics
// report the same values withUTMParams appends.
export const UTM_SOURCE = "tailormed";
export const UTM_MEDIUM = "app";

// Matches an RFC 3986 "scheme:" prefix. ("example.com:8080" also matches since "." is a
// legal scheme char — fine for AP data, which has no port-without-protocol URLs.)
const URI_SCHEME_REGEX = /^[a-z][a-z0-9+.-]*:/i;
export const hasUriScheme = (url) => typeof url === "string" && URI_SCHEME_REGEX.test(url);

// True for an http/https scheme (case-insensitive).
const HTTP_SCHEME_REGEX = /^https?:/i;
export const isHttpUrl = (url) => typeof url === "string" && HTTP_SCHEME_REGEX.test(url);

// True when the URL has a scheme that is NOT http(s) (mailto:, tel:, …) — skip UTM tagging on these.
export const hasNonHttpScheme = (url) => hasUriScheme(url) && !isHttpUrl(url);

// Prepend http:// unless the link already has an http(s) scheme (case-insensitive).
export const getClickableLink = (link) => (isHttpUrl(link) ? link : `http://${link}`);

// Set a query param only when the value is meaningful and the key is absent — keeps
// composition additive/idempotent (existing params, e.g. Paytient's, survive). Mutates params.
export const setIfAbsent = (params, key, value) => {
  if (value != null && value !== "" && !params.has(key)) {
    params.set(key, value);
  }
};

// Hostname only of an external URL ("https://x.org/a?b=1" → "x.org"); undefined for
// falsy/non-string/non-http/unparseable. Strips the query → no PHI leak (e.g. utm_patientid).
export const getUrlHost = (url) => {
  if (!url || typeof url !== "string" || hasNonHttpScheme(url)) return undefined;
  try {
    return new URL(getClickableLink(url)).host;
  } catch {
    return undefined;
  }
};

// additional_info for analytics so in-app AP link clicks reconcile 1:1 with the program's
// UTM analytics. utm_content === the per-call-site contentKey (single source for both the
// URL tag and the event).
export const utmReconciliationInfo = (contentKey, url) => ({
  utm_content: contentKey,
  utm_source: UTM_SOURCE,
  utm_medium: UTM_MEDIUM,
  destination_host: getUrlHost(url)
});

// Append utm_source/utm_medium/utm_content (+ any extra option keys) to an external URL.
// Additive + idempotent (setIfAbsent never overrides existing params, so Paytient's stay).
// Input returned unchanged for falsy/non-string/non-http; http:// prepended when schemeless;
// on parse failure the http-prefixed string is returned.
export const withUTMParams = (url, options) => {
  if (!url || typeof url !== "string") return url;
  if (hasNonHttpScheme(url)) return url;

  const safeOptions = options || {};
  const { content, source = UTM_SOURCE, medium = UTM_MEDIUM, ...extras } = safeOptions;
  const clickable = getClickableLink(url);

  try {
    const urlObj = new URL(clickable);
    const params = urlObj.searchParams;

    // Caller's extras first, so a caller can override defaults via { utm_source: "newsletter" } etc.
    Object.entries(extras).forEach(([key, value]) => setIfAbsent(params, key, value));

    // Defaults land only if no caller extra and no pre-existing URL param won.
    setIfAbsent(params, "utm_source", source);
    setIfAbsent(params, "utm_medium", medium);
    setIfAbsent(params, "utm_content", content);

    return urlObj.toString();
  } catch {
    return clickable;
  }
};
